import { Button } from "@volca/ui/components/ui/button";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { PageContainer } from "@volca/ui/components/ui/page-components";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Text } from "@volca/ui/components/ui/text";
import { getProductCapabilities } from "@/business/product-capabilities";
import { useGetBusiness } from "@/business/query";
import { useBusiness } from "@/core/business";
import { FEATURE_FLAG_NAMES } from "@/core/feature-flags/feature-flag-name";
import { useFeatureFlag } from "@/core/feature-flags/use-feature-flag";
import { useGetMyUser } from "@/user/query";
import { ProfitabilityContent } from "./profitability-content";
import { ProfitabilityPreview } from "./profitability-preview";

const PageMessage = ({ children }: { children: string }) => (
  <PageContainer>
    <Text tone="muted">{children}</Text>
  </PageContainer>
);

const ProfitabilityPage = () => {
  const { businessId, isLoading } = useBusiness();
  const { data: user, isPending } = useGetMyUser();
  const { enabled: documentsOnly, isLoading: isFlagLoading } = useFeatureFlag(
    FEATURE_FLAG_NAMES.profitability_documents_only,
  );
  const {
    data: business,
    isLoading: isBusinessLoading,
    isError,
    isFetching,
    refetch,
  } = useGetBusiness(businessId);

  if (isPending || isLoading || isBusinessLoading || isFlagLoading)
    return (
      <PageContainer>
        <Spinner layout="centered" aria-label="Loading Job Costing" />
      </PageContainer>
    );
  if (!businessId)
    return <PageMessage>Select a business to view its reports.</PageMessage>;

  if (isError || !business)
    return (
      <PageContainer variant="workspace">
        <ErrorState
          title="Business details could not load"
          description="Try again in a moment."
          retrying={isFetching}
        >
          <Button onClick={() => void refetch()} disabled={isFetching}>
            Try again
          </Button>
        </ErrorState>
      </PageContainer>
    );

  const capabilities = getProductCapabilities({
    user,
    business,
    settings: business.enabled_products,
    documentsOnly,
  });
  if (capabilities.profitability.canOpenReports)
    return <ProfitabilityContent key={businessId} businessId={businessId} />;

  if (
    capabilities.enabled.profitability &&
    capabilities.profitability.canAccess
  )
    return <PageMessage>Reports are being set up.</PageMessage>;

  return <ProfitabilityPreview />;
};

export default ProfitabilityPage;
